import { Injectable } from '@angular/core';
import { HistoricalDataService } from '../historical-data/historical-data.service';
import moment from 'moment';

export enum ClinicalRecordTab {
  ConsultationSummary = 'consultation_summary',
  Observations = 'observations',
  MedicationRequest = 'medication_request'
}

export interface TabState {
  startDate: moment.Moment;
  endDate: moment.Moment;
  showViewMore: boolean;
  hasData: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ClinicalRecordsService {
  private readonly MONTH_INTERVAL = 3;

  private tabStates: { [key: string]: TabState } = {};

  constructor(private readonly historicalDataService: HistoricalDataService) {

  }

  initTab(tabKey: string): void {
    if (!this.tabStates[tabKey]) {
      const endDate = moment().endOf('day'); // today, end of day
      const startDate = moment(endDate).subtract(this.MONTH_INTERVAL, 'months').startOf('day');

      this.tabStates[tabKey] = {
        startDate,
        endDate,
        showViewMore: true,
        hasData: true
      };
    }
  }

  getDateRange(tabKey: string): { startDate: string; endDate: string } {
    this.ensureTabInitialized(tabKey);
    const { startDate, endDate } = this.tabStates[tabKey];
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  }

  getNextRange(tabKey: string): { startDate: string; endDate: string } {
    this.ensureTabInitialized(tabKey);

    const currentStart = this.tabStates[tabKey].startDate.clone();
    const newEnd = currentStart.subtract(1, 'day').endOf('day');
    const newStart = newEnd.clone().subtract(this.MONTH_INTERVAL, 'months').startOf('day');

    this.tabStates[tabKey].startDate = newStart;
    this.tabStates[tabKey].endDate = newEnd;

    return {
      startDate: newStart.toISOString(),
      endDate: newEnd.toISOString()
    };
  }

  setViewMoreVisibility(tabKey: string, visible: boolean): void {
    this.ensureTabInitialized(tabKey);
    this.tabStates[tabKey].showViewMore = visible;
  }

  setHasData(tabKey: string, status: boolean): void {
    this.ensureTabInitialized(tabKey);
    this.tabStates[tabKey].hasData = status;
  }

  shouldShowViewMore(tabKey: string): boolean {
    this.ensureTabInitialized(tabKey);
    return this.tabStates[tabKey].showViewMore;
  }

  shouldShowHasData(tabKey: string): boolean {
    this.ensureTabInitialized(tabKey);
    return this.tabStates[tabKey].hasData;
  }

  resetAllTabStates(): void {
    this.tabStates = {};
  }

  private ensureTabInitialized(tabKey: string): void {
    if (!this.tabStates[tabKey]) {
      this.initTab(tabKey);
    }
  }
}
